import { useState, useEffect } from 'react';
import { Calendar, ArrowRight, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/blog-posts`);
      setPosts(res.data.posts || []);
    } catch (err) {
      setError('Unable to load blog posts.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[#f7f6f3]" data-testid="blog-page">
      <div className="container mx-auto px-4 md:px-8 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-14">
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#1a7a7a] mb-3 font-medium"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Blog & Resources
          </p>
          <h1
            className="text-4xl md:text-5xl font-bold tracking-tight text-[#1a1a1a] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Insights & Stories
          </h1>
          <p className="text-gray-500 text-base max-w-xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Expert guidance on global citizenship, tax strategy, and building a life without borders.
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin text-[#1a7a7a]" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-16" data-testid="blog-error">
            <p className="text-gray-400 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>{error}</p>
            <a
              href="https://www.globalcitizenconsult.com/blog"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="blog-external-link"
              className="inline-flex items-center gap-2 text-[#1a7a7a] hover:underline font-medium text-sm"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Visit our blog on the main site <ExternalLink size={14} />
            </a>
          </div>
        )}

        {/* Posts grid */}
        {!loading && !error && posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="blog-posts-grid">
            {posts.map((post, idx) => (
              <article
                key={post.id || idx}
                data-testid={`blog-post-${idx}`}
                className="group rounded-2xl bg-white border border-gray-200 overflow-hidden hover:border-[#1a7a7a]/30 hover:shadow-lg transition-all duration-500"
              >
                {/* Featured image */}
                {post.featured_image && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  {/* Date */}
                  {post.publish_date && (
                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <Calendar size={12} />
                      {formatDate(post.publish_date)}
                    </div>
                  )}
                  {/* Title */}
                  <h2
                    className="text-xl font-semibold text-[#1a1a1a] mb-3 group-hover:text-[#1a7a7a] transition-colors"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {post.title}
                  </h2>
                  {/* Excerpt */}
                  {post.meta_description && (
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {post.meta_description}
                    </p>
                  )}
                  {/* Read more */}
                  {post.url && (
                    <a
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#1a7a7a] text-sm font-medium hover:gap-2 transition-all"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      Read more <ArrowRight size={14} />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && posts.length === 0 && (
          <div className="text-center py-16" data-testid="blog-empty">
            <p className="text-gray-400 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              No blog posts found. Check back soon!
            </p>
            <a
              href="https://www.globalcitizenconsult.com/blog"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#1a7a7a] hover:underline font-medium text-sm"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Visit our blog on the main site <ExternalLink size={14} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
